import {differenceInCalendarDays} from 'date-fns'

import {businessCalendarDate} from 'src/api/business-time'

import {parseApiDate} from '../jobs/job-schedule-format'
import {weekStartFromWeekOffset} from './time-navigation-helpers'
import type {
    DispatchAssignment,
    DispatchTimelineData,
    DispatchTimeSlot,
    HourlyLoad,
    JobTimeSpan,
    ScheduleDate,
    UnassignedJob,
    ViewRange,
} from './types'
import {
    buildWorkDaySlots,
    businessNowHour,
    formatHourLabel,
    SLOTS_PER_DAY,
    WORK_DAY_START_HOUR,
} from './work-day'

function startOfToday(): Date {
    return businessCalendarDate()
}

function addDays(date: Date, days: number): Date {
    const next = new Date(date)
    next.setDate(date.getDate() + days)
    return next
}

function isSameCalendarDay(a: Date, b: Date): boolean {
    return (
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate()
    )
}

/** Rolling 30-day horizon for month view on the dispatch board. */
export const MONTH_VIEW_DAYS = 30

export function daysInViewRange(viewRange: ViewRange): number {
    switch (viewRange) {
        case 'day':
            return 1
        case '2days':
            return 2
        case 'week':
            return 7
        case 'month':
            return MONTH_VIEW_DAYS
    }
}

export function slotMinWidthForView(viewRange: ViewRange): number {
    switch (viewRange) {
        case 'day':
            return 52
        case '2days':
            return 40
        case 'week':
            return 26
        case 'month':
            return 18
    }
}

export function buildVisibleDays(dayCount: number, anchorDate = startOfToday()): ScheduleDate[] {
    const today = startOfToday()
    const days: ScheduleDate[] = []
    for (let offset = 0; offset < dayCount; offset++) {
        const date = addDays(anchorDate, offset)
        const weekdayShort = date.toLocaleDateString('en-US', {weekday: 'short'})
        const monthLabel = date.toLocaleDateString('en-US', {month: 'short'})
        const dayOfMonth = date.getDate()
        days.push({
            label: date.toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'short',
                day: 'numeric',
                year: 'numeric',
            }),
            weekdayShort,
            dayOfMonth,
            monthLabel,
            isToday: isSameCalendarDay(date, today),
        })
    }
    return days
}

export function formatVisibleRangeLabel(days: ScheduleDate[]): string {
    if (days.length === 0) return ''
    if (days.length === 1) return days[0].label
    const first = days[0]
    const last = days[days.length - 1]
    const anchorYear = startOfToday().getFullYear()
    return `${first.weekdayShort}, ${first.monthLabel} ${first.dayOfMonth} – ${last.weekdayShort}, ${last.monthLabel} ${last.dayOfMonth}, ${anchorYear}`
}

export function buildViewTimeSlots(
    viewRange: ViewRange,
    nowHour = businessNowHour(),
): DispatchTimeSlot[] {
    const dayCount = daysInViewRange(viewRange)
    const daySlots = buildWorkDaySlots(nowHour)
    const slots: DispatchTimeSlot[] = []

    for (let dayOffset = 0; dayOffset < dayCount; dayOffset++) {
        for (const slot of daySlots) {
            const hour = Number(slot.key.replace('h', ''))
            slots.push({
                key: `d${dayOffset}-${slot.key}`,
                label: formatHourLabel(hour),
                isNow: dayOffset === 0 && slot.isNow,
                dayOffset,
                isDayStart: hour === WORK_DAY_START_HOUR,
            })
        }
    }

    return slots
}

export function toGlobalSlotIndex(span: JobTimeSpan): number {
    const dayOffset = span.dayOffset ?? 0
    return dayOffset * SLOTS_PER_DAY + span.startSlotIndex
}

/** Jobs without a schedule sort after all scheduled jobs. */
export function toGlobalSlotIndexOrLast(span?: JobTimeSpan): number {
    if (!span) return Number.POSITIVE_INFINITY
    return toGlobalSlotIndex(span)
}

export function toGlobalEndSlotIndex(span: JobTimeSpan): number {
    const dayOffset = span.dayOffset ?? 0
    return dayOffset * SLOTS_PER_DAY + span.endSlotIndex
}

export function projectSpanForView(span: JobTimeSpan, dayCount: number): JobTimeSpan {
    const dayOffset = span.dayOffset ?? 0
    return {
        startSlotIndex: dayOffset * SLOTS_PER_DAY + span.startSlotIndex,
        endSlotIndex: dayOffset * SLOTS_PER_DAY + span.endSlotIndex,
        continuesFromPriorDay: dayOffset === 0 ? span.continuesFromPriorDay : undefined,
        continuesIntoNextDay: dayOffset === dayCount - 1 ? span.continuesIntoNextDay : undefined,
        dayOffset,
        daySpan: span.daySpan,
        openEnded: span.openEnded,
    }
}

function isSpanInView(span: JobTimeSpan, dayCount: number): boolean {
    return (span.dayOffset ?? 0) < dayCount
}

function maxDayOffset(data: DispatchTimelineData): number {
    const offsets = [
        ...data.unassignedJobs
            .filter((job) => job.span != null)
            .map((job) => job.span!.dayOffset ?? 0),
        ...data.assignments.map((assignment) => assignment.span.dayOffset ?? 0),
    ]
    return offsets.length > 0 ? Math.max(...offsets) : 0
}

function computeHourlyLoad(
    unassignedJobs: UnassignedJob[],
    assignments: DispatchAssignment[],
    timeSlots: DispatchTimeSlot[],
): HourlyLoad[] {
    const spans = [
        ...unassignedJobs.filter((job) => job.span != null).map((job) => job.span!),
        ...assignments.map((assignment) => assignment.span),
    ]

    return timeSlots.map((slot, index) => {
        const value = spans.filter(
            (span) => span.startSlotIndex <= index && index <= span.endSlotIndex,
        ).length
        return {slotKey: slot.key, value}
    })
}

export function projectTimelineData(
    canonical: DispatchTimelineData,
    viewRange: ViewRange,
    anchorOffset = 0,
): DispatchTimelineData {
    const dayCount = daysInViewRange(viewRange)
    let anchorDate: Date
    let viewDayOriginOffset: number

    if (canonical.scheduleWindowStart != null) {
        anchorDate = parseApiDate(canonical.scheduleWindowStart)
        viewDayOriginOffset = 0
    } else if (viewRange === 'week') {
        const today = startOfToday()
        const weekStart = weekStartFromWeekOffset(anchorOffset, today)
        const dayShift = Math.max(0, differenceInCalendarDays(weekStart, today))
        anchorDate = weekStart
        viewDayOriginOffset = differenceInCalendarDays(weekStart, addDays(today, dayShift))
    } else {
        anchorDate = addDays(startOfToday(), anchorOffset)
        viewDayOriginOffset = anchorOffset
    }

    const visibleDays = buildVisibleDays(dayCount, anchorDate)
    const scheduleDays = buildVisibleDays(maxDayOffset(canonical) + 1, anchorDate)
    const slotsPerDay = SLOTS_PER_DAY
    const timeSlots = buildViewTimeSlots(viewRange)

    const unassignedJobs = canonical.unassignedJobs.map((job) => ({
        ...job,
        span: job.span ? projectSpanForView(job.span, dayCount) : undefined,
    }))

    const assignments = canonical.assignments
        .filter((assignment) => isSpanInView(assignment.span, dayCount))
        .map((assignment) => ({
            ...assignment,
            span: projectSpanForView(assignment.span, dayCount),
        }))

    const visibleUnassignedForLoad = canonical.unassignedJobs
        .filter((job) => job.span != null && isSpanInView(job.span!, dayCount))
        .map((job) => ({
            ...job,
            span: projectSpanForView(job.span!, dayCount),
        }))

    return {
        ...canonical,
        scheduleDate: {
            ...visibleDays[0],
            label: formatVisibleRangeLabel(visibleDays),
            isToday: visibleDays[0].isToday,
        },
        viewRange,
        scheduleDays,
        visibleDays,
        viewDayOriginOffset,
        slotsPerDay,
        slotColumnMinWidth: slotMinWidthForView(viewRange),
        timeSlots,
        unassignedJobs,
        assignments,
        hourlyLoad: computeHourlyLoad(visibleUnassignedForLoad, assignments, timeSlots),
    }
}

export function scheduleDayForSpan(
    span: JobTimeSpan,
    visibleDays: ScheduleDate[],
): ScheduleDate | undefined {
    return visibleDays[span.dayOffset ?? 0]
}
